/**
 * Audio Studio API client for api-audio (`beatly-adstudio-sound` FastAPI, `/studio/v1`).
 *
 * Contract: beatly-adstudio-sound/docs/AUDIO_STUDIO_API.md (v1).
 * - Studio endpoints live under `{VITE_AUDIO_STUDIO_API_URL}/studio/v1` and take the api-v2 JWT as a Bearer token
 *   (same token the cowork client sends — `getTokenFromStorage`).
 * - The curated catalog (`/studio/v1/library/*`, `/studio/v1/playlists*`, `/studio/v1/audio/*`, `/studio/v1/image/*`) is public.
 * - Every media URL the service returns may be relative to the API origin; `resolveMediaUrl` makes them absolute so
 *   `<audio>` / `<img>` can load them from the Omni origin.
 */
import { getTokenFromStorage } from '@/utils/auth';
import { getStaffScopeTeam, isClientTeamId } from '@/cowork/staffScope';

/**
 * VITE_AUDIO_STUDIO_API_URL without a trailing slash. Only development falls back to the local service; a build
 * without it logs an error (instead of throwing at import, which would take down every page embedding soundtracks)
 * and Studio calls then fail visibly.
 */
function resolveAudioStudioApiUrl(): string {
  const apiUrl = import.meta.env.VITE_AUDIO_STUDIO_API_URL as string | undefined;
  if (apiUrl) return apiUrl.replace(/\/+$/, '');
  if (import.meta.env.MODE === 'development') return 'http://localhost:8001';
  console.error('[AudioStudio] VITE_AUDIO_STUDIO_API_URL is not configured.');
  return '';
}

export const AUDIO_STUDIO_API_URL: string = resolveAudioStudioApiUrl();

export const STUDIO_BASE_PATH = '/studio/v1';

/** The country TikTok trends open on (the Audio Studio's home market). */
export const DEFAULT_TREND_REGION = 'GB';

// ─── Types (mirror the contract) ────────────────────────────────────────────

export type Visibility = 'private' | 'members' | 'team';
export type TrackSource = 'prompt' | 'preset' | 'trend_lookalike' | 'library';
export type Tempo = 'slow' | 'medium' | 'fast';
export type DurationHint = 6 | 15 | 30 | 60;

export interface GenerateSettings {
  preset_id?: string | null;
  genre?: string | null;
  mood?: string | null;
  tempo?: Tempo | null;
  energy?: number | null;
  instruments?: string[];
  vocals?: 'instrumental' | 'vocals';
  lyrics?: string | null;
  duration_hint_s?: DurationHint | null;
  use_case?: string | null;
}

export interface StudioTrack {
  track_id: string;
  source: TrackSource;
  title: string;
  description: string;
  genre: string | null;
  mood: string | null;
  tempo: Tempo | null;
  music_type: 'Instrumental' | 'Singing';
  duration_s: number | null;
  audio_url: string;
  cover_url: string | null;
  prompt: string;
  user_prompt: string | null;
  settings: GenerateSettings;
  created_at: string;
  owner_id: string;
  owner_name: string | null;
  team_id: string;
  brand_id: string | null;
  visibility: Visibility;
  allowed_user_ids: string[];
  is_owner: boolean;
  share_enabled: boolean;
  share_token: string | null;
  trend_ref: { trend_id: string; title: string; artist: string | null } | null;
}

export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface StudioJob {
  job_id: string;
  kind: 'generate' | 'trend_lookalike' | 'bulk';
  status: JobStatus;
  progress: number;
  requested: number;
  tracks: StudioTrack[];
  error: string | null;
  created_at: string;
  updated_at: string;
  /** Name the service picked for the sound(s) being made — additive, older services omit it. */
  title?: string | null;
}

/**
 * Where a picked track applies: the whole campaign (`target_id` = the campaign id), one beat (brief) or one
 * collaboration. One track per target — attaching replaces the target's previous pick; the most specific wins
 * (collaboration > beat > campaign > the public library link every brief carries by default).
 */
export type AttachmentTargetType = 'campaign' | 'beat' | 'collaboration';

export interface Attachment {
  attachment_id: string;
  track_id: string;
  track_source: 'library' | 'studio';
  target_type: AttachmentTargetType;
  target_id: string;
  campaign_id: string;
  note: string | null;
  created_by: string;
  created_at: string;
  track: StudioTrack;
  /** The track's public page creators open from the brief — additive, older services omit it. */
  public_url?: string | null;
}

export interface CreateAttachmentBody {
  track_id: string;
  track_source: 'library' | 'studio';
  target_type: AttachmentTargetType;
  target_id: string;
  campaign_id: string;
  note?: string;
}

export interface AudioTrend {
  trend_id: string;
  rank: number;
  rank_change: number | null;
  title: string;
  artist: string | null;
  duration_s: number | null;
  cover_url: string | null;
  preview_url: string | null;
  tiktok_url: string | null;
  usage_count: number | null;
  growth_pct: number | null;
  genre_hint: string | null;
  mood_hint: string | null;
  bpm_hint: number | null;
  related_hashtags: string[];
  is_commercial_safe: boolean | null;
  // ── Trends v2 (TikTok Commercial Music Library). Optional: older payloads / providers omit them. ──
  commercial_music_id?: string | null;
  /** TikTok genre labels as given, e.g. ["Pop", "Hip Hop"]. */
  genres?: string[];
  /** Oldest → newest, ≤ 30 days. */
  history?: TrendHistoryPoint[];
  views_latest?: number | null;
  views_7d?: number | null;
  trend_status?: TrendStatus | null;
  /** ≤ 20 top TikTok videos using the song. */
  top_videos?: TrendTopVideo[];
  clips?: { trending: TrendSongClip | null; full: TrendSongClip | null } | null;
}

export type TrendStatus = 'rising_fast' | 'rising' | 'steady' | 'falling' | 'new';
export interface TrendHistoryPoint { date: string; rank: number | null; views: number | null }
export interface TrendTopVideo {
  video_id: string;
  embed_url: string;
  share_url: string | null;
  /** From TikTok oEmbed (the service fills the first 3 videos per song); optional, may be missing or null. */
  thumbnail_url?: string | null;
  caption?: string | null;
  /** The creator's handle (with or without a leading "@"). */
  author?: string | null;
}
/** A TikTok song clip — `song_clip_id` is what TikTok business tools attach when publishing. */
export interface TrendSongClip { song_clip_id: string; preview_url: string | null; duration_s: number | null }

export interface AudioTrendsResponse {
  source: string;
  region: string;
  period_days: number;
  fetched_at: string;
  items: AudioTrend[];
}

export interface PresetOption { id: string; label: string; hint?: string }
export interface PresetTemplate { id: string; label: string; description: string; emoji?: string; values: GenerateSettings }
export interface StudioPresets {
  genres: PresetOption[];
  moods: PresetOption[];
  tempos: PresetOption[];
  energies: PresetOption[];
  instruments: PresetOption[];
  durations: PresetOption[];
  use_cases: PresetOption[];
  templates: PresetTemplate[];
}

export interface GenerateBody extends GenerateSettings {
  prompt?: string;
  variations?: number;
  title?: string;
  brand_id?: string;
}

export interface LookalikeBody {
  variations?: number;
  vocals?: 'instrumental' | 'vocals';
  duration_hint_s?: DurationHint;
  extra_prompt?: string;
  /** Omni brand the sound is made for. */
  brand_id?: string;
}

export interface TeamMemberRow { user_id: string; name: string; email: string | null; role: string | null }
export interface TeamMembersResponse { team_id: string; members: TeamMemberRow[]; me: string }
export interface StudioMe { user_id: string; team_id: string; is_admin: boolean }
export interface ShareResponse { track_id: string; share_enabled: boolean; share_token: string | null }

/** A named group of the team's studio sounds (e.g. per campaign or brand). Team-scoped. */
export interface CollectionSummary {
  collection_id: string;
  name: string;
  track_count: number;
  /** Up to 4 signed covers of the collection's sounds (for the mosaic). */
  cover_urls: string[];
  /** Omni brand the collection is for (null / missing = no brand). */
  brand_id?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** GET /collections/{id}: the summary plus its sounds (newest added first; only the ones the viewer can see). */
export interface CollectionDetail extends CollectionSummary {
  tracks: StudioTrack[];
}

export type PublicStudioTrack = Pick<StudioTrack, 'title' | 'description' | 'genre' | 'mood' | 'duration_s' | 'audio_url' | 'cover_url'> & {
  track_id?: string;
  /** Library tracks only. */
  tempo?: string | null;
  artist?: string | null;
};

// Curated library (existing public endpoints)
export interface LibraryCsvMetadata { title?: string; genre?: string; mood?: string; tempo?: string; description?: string; length?: string }
export interface LibraryTrack {
  track_id?: string;
  track_name?: string;
  artist?: string;
  job_id?: string;
  filename?: string;
  imgurl?: string;
  gcs_public_url?: string;
  music_type?: string;
  csv_metadata?: LibraryCsvMetadata;
  generation_metadata?: { generated_at?: string };
}
export interface LibraryTracksResponse { tracks: LibraryTrack[]; total: number }
export interface LibraryFilterOptions { genres: string[]; moods: string[]; tempos: string[] }
export interface LibraryPlaylist {
  playlist_id?: string;
  name?: string;
  description?: string;
  image_url?: string;
  track_ids?: string[];
  track_count?: number;
  tracks?: LibraryTrack[];
}

// ─── URL + auth helpers ─────────────────────────────────────────────────────

/** Absolute URL for a media path the service returned (relative → against the API origin). */
export function resolveMediaUrl(url: string | null | undefined, base: string = AUDIO_STUDIO_API_URL): string | null {
  if (!url) return null;
  if (/^(https?:)?\/\//i.test(url) || /^(data|blob):/i.test(url)) return url;
  const root = base.replace(/\/+$/, '');
  return url.startsWith('/') ? `${root}${url}` : `${root}/${url}`;
}

type QueryValue = string | number | boolean | null | undefined;

/** Full URL for an API path with a query string (empty / null values dropped). */
export function buildApiUrl(path: string, query?: Record<string, QueryValue>, base: string = AUDIO_STUDIO_API_URL): string {
  const root = base.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query || {})) {
    if (v === undefined || v === null || v === '') continue;
    qs.append(k, String(v));
  }
  const s = qs.toString();
  return `${root}${p}${s ? `?${s}` : ''}`;
}

/** Authorization header from the stored api-v2 session (none when signed out). */
export function authHeaders(): Record<string, string> {
  const token = getTokenFromStorage();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Staff inside the back office act on the client picked in the scope bar (`?team_id=`, admin tokens only). */
function staffTeamQuery(): Record<string, string> {
  const t = getStaffScopeTeam();
  return isClientTeamId(t) ? { team_id: t } : {};
}

export class AudioStudioApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AudioStudioApiError';
    this.status = status;
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: unknown; error?: unknown };
    const d = body?.detail ?? body?.error;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => (x as { msg?: string })?.msg || JSON.stringify(x)).join('; ');
  } catch {
    /* non-JSON body */
  }
  return `Request failed (${res.status})`;
}

interface RequestOptions {
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** Studio routes need the Bearer token; the public catalog and share pages do not. */
  auth?: boolean;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, query, auth = true } = opts;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (auth) Object.assign(headers, authHeaders());
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const url = buildApiUrl(path, auth ? { ...staffTeamQuery(), ...(query || {}) } : query);
  const res = await fetch(url, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  if (!res.ok) throw new AudioStudioApiError(await readError(res), res.status);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const studio = (p: string) => `${STUDIO_BASE_PATH}${p}`;

// ─── Normalisers (absolute media URLs) ──────────────────────────────────────

export function normalizeTrack(t: StudioTrack): StudioTrack {
  return {
    ...t,
    audio_url: resolveMediaUrl(t.audio_url) || '',
    cover_url: resolveMediaUrl(t.cover_url),
    allowed_user_ids: t.allowed_user_ids || [],
    settings: t.settings || {},
  };
}

export function normalizeJob(j: StudioJob): StudioJob {
  return { ...j, tracks: (j.tracks || []).map(normalizeTrack) };
}

function normalizeAttachment(a: Attachment): Attachment {
  return { ...a, track: a.track ? normalizeTrack(a.track) : a.track };
}

export function normalizeCollection(c: CollectionSummary): CollectionSummary {
  return { ...c, cover_urls: (c.cover_urls || []).map((u) => resolveMediaUrl(u)).filter((u): u is string => Boolean(u)).slice(0, 4) };
}

function normalizeCollectionDetail(c: CollectionDetail): CollectionDetail {
  return { ...c, ...normalizeCollection(c), tracks: (c.tracks || []).map(normalizeTrack) };
}

function normalizeClip(c: TrendSongClip | null | undefined): TrendSongClip | null {
  return c && c.song_clip_id ? { ...c, preview_url: resolveMediaUrl(c.preview_url) } : null;
}

/** Fills the v2 fields with empty defaults so older payloads render the same UI (just with less in it). */
export function normalizeTrend(t: AudioTrend): AudioTrend {
  return {
    ...t,
    cover_url: resolveMediaUrl(t.cover_url),
    preview_url: resolveMediaUrl(t.preview_url),
    related_hashtags: t.related_hashtags || [],
    commercial_music_id: t.commercial_music_id ?? null,
    genres: (t.genres || []).filter(Boolean),
    history: Array.isArray(t.history) ? t.history.filter((p) => p && p.date) : [],
    views_latest: t.views_latest ?? null,
    views_7d: t.views_7d ?? null,
    trend_status: t.trend_status ?? null,
    top_videos: Array.isArray(t.top_videos) ? t.top_videos.filter((v) => v && v.video_id).slice(0, 20) : [],
    clips: { trending: normalizeClip(t.clips?.trending), full: normalizeClip(t.clips?.full) },
  };
}

// ─── Library helpers ────────────────────────────────────────────────────────

export function libraryTrackKey(t: LibraryTrack): string {
  return t.track_id || `${t.job_id}/${t.filename}`;
}

export function libraryTrackTitle(t: LibraryTrack): string {
  return t.csv_metadata?.title || t.track_name || 'Untitled';
}

/** Signed catalog audio via `/studio/v1/audio/{job}/{filename}`; falls back to the stored public GCS URL. */
export function libraryAudioUrl(t: LibraryTrack): string | null {
  if (t.job_id && t.filename) return buildApiUrl(studio(`/audio/${encodeURIComponent(t.job_id)}/${encodeURIComponent(t.filename)}`));
  return t.gcs_public_url || null;
}

/**
 * Prefer absolute CDN `imgurl` from the API. Private `music/.../covers/...` keys still go through
 * the resize proxy during rollout; other values are absolutised against the API origin.
 */
export function libraryCoverUrl(t: LibraryTrack, width = 100): string | null {
  const img = t.imgurl || '';
  if (/^(https?:)?\/\//i.test(img) || /^(data|blob):/i.test(img)) {
    return resolveMediaUrl(img);
  }
  if (img && t.job_id && img.includes('/covers/')) {
    const filename = img.split('/covers/')[1]?.split('?')[0];
    if (filename) return buildApiUrl(studio(`/image/${encodeURIComponent(t.job_id)}/${filename}`), { width, quality: 85 });
  }
  return resolveMediaUrl(img);
}

/** "2:30" / "02:30" → 150 (library CSV lengths); null when unparsable. */
function lengthSeconds(length: string | undefined): number | null {
  const m = /^(\d+):(\d{1,2})$/.exec((length || '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** A curated library track as the public sound page shows it. */
export function libraryToPublicTrack(t: LibraryTrack): PublicStudioTrack {
  const csv = t.csv_metadata || {};
  return {
    track_id: t.track_id,
    title: libraryTrackTitle(t),
    description: csv.description || null,
    genre: csv.genre || null,
    mood: csv.mood || null,
    tempo: csv.tempo || null,
    artist: t.artist || null,
    duration_s: lengthSeconds(csv.length),
    audio_url: libraryAudioUrl(t) || '',
    cover_url: libraryCoverUrl(t, 800),
  } as PublicStudioTrack;
}

/** The same audio URL, served as a file to save ("<Title>.mp3") instead of played inline. */
export function downloadUrl(audioUrl: string): string {
  return `${audioUrl}${audioUrl.includes('?') ? '&' : '?'}download=1`;
}

/**
 * Prefer absolute CDN `image_url` from the API. Bare private S3 keys still use the
 * `/playlists/{id}/image` proxy (optional width) during rollout.
 */
export function playlistCoverUrl(p: LibraryPlaylist, width = 400): string | null {
  if (!p.playlist_id || !p.image_url) return null;
  const raw = p.image_url;
  if (/^(https?:)?\/\//i.test(raw) || /^(data|blob):/i.test(raw)) {
    return resolveMediaUrl(raw);
  }
  return buildApiUrl(studio(`/playlists/${encodeURIComponent(p.playlist_id)}/image`), { width });
}

/** Seconds from the catalog's `length` field ("m:ss" or seconds). */
export function libraryDurationSeconds(t: LibraryTrack): number | null {
  const raw = t.csv_metadata?.length;
  if (!raw) return null;
  const m = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

/** The public share page for a studio track. */
export function soundShareUrl(trackId: string, token: string, origin: string = window.location.origin): string {
  return `${origin}/share/sound/${encodeURIComponent(trackId)}?token=${encodeURIComponent(token)}`;
}

/** Download a media URL as a file (cross-origin `download` attributes are ignored, so go through a blob). */
export async function downloadAudio(url: string, filename: string): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) throw new AudioStudioApiError(`Download failed (${res.status})`, res.status);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = /\.(mp3|wav|m4a|ogg)$/i.test(filename) ? filename : `${filename}.mp3`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

// ─── API ────────────────────────────────────────────────────────────────────

export interface ListTracksParams { scope?: 'mine' | 'team'; q?: string; source?: TrackSource; brand_id?: string; limit?: number; offset?: number }
export interface LibraryTracksParams { genre?: string; mood?: string; tempo?: string; music_type?: string; search?: string; limit?: number; offset?: number }
export type AttachmentsQuery =
  | { target_type: AttachmentTargetType; target_id: string }
  | { campaign_id: string }
  | { track_id: string };

/** Typed HTTP client for api-audio `/studio/v1` verb paths (create / search / update / archive). */
export const audioStudioApi = {
  me: () => request<StudioMe>('GET', studio('/me')),
  presets: () => request<StudioPresets>('GET', studio('/presets')),
  generate: async (body: GenerateBody) => normalizeJob(await request<StudioJob>('POST', studio('/track/create'), { body })),
  job: async (jobId: string) => normalizeJob(await request<StudioJob>('GET', studio(`/job/${encodeURIComponent(jobId)}`))),
  jobs: async (limit = 20) => {
    const r = await request<{ jobs: StudioJob[] }>('POST', studio('/jobs/search'), { query: { limit } });
    return { jobs: (r.jobs || []).map(normalizeJob) };
  },
  tracks: async (params: ListTracksParams = {}) => {
    const r = await request<{ tracks: StudioTrack[]; total: number }>('POST', studio('/tracks/search'), { query: { scope: 'team', limit: 50, offset: 0, ...params } });
    return { tracks: (r.tracks || []).map(normalizeTrack), total: r.total ?? 0 };
  },
  track: async (id: string) => normalizeTrack(await request<StudioTrack>('GET', studio(`/track/${encodeURIComponent(id)}`))),
  /** `brand_id: ""` clears the brand. */
  updateTrack: async (id: string, patch: { title?: string; description?: string; visibility?: Visibility; allowed_user_ids?: string[]; brand_id?: string }) =>
    normalizeTrack(await request<StudioTrack>('POST', studio(`/track/${encodeURIComponent(id)}/update`), { body: patch })),
  deleteTrack: (id: string) => request<void>('POST', studio(`/track/${encodeURIComponent(id)}/archive`)),
  shareTrack: (id: string, body: { enabled?: boolean; rotate?: boolean }) =>
    request<ShareResponse>('POST', studio(`/track/${encodeURIComponent(id)}/share`), { body }),
  teamMembers: () => request<TeamMembersResponse>('GET', studio('/team/members')),
  createAttachment: async (body: CreateAttachmentBody) => normalizeAttachment(await request<Attachment>('POST', studio('/attachment/create'), { body })),
  attachments: async (query: AttachmentsQuery) => {
    const r = await request<{ attachments: Attachment[] }>('POST', studio('/attachments/search'), { query: query as Record<string, string> });
    return { attachments: (r.attachments || []).map(normalizeAttachment) };
  },
  deleteAttachment: (id: string) => request<void>('POST', studio(`/attachment/${encodeURIComponent(id)}/archive`)),
  trends: async (params: { region?: string; period?: number; limit?: number } = {}) => {
    const r = await request<AudioTrendsResponse>('GET', studio('/trends/audio'), { query: { region: DEFAULT_TREND_REGION, period: 7, limit: 50, ...params } });
    return { ...r, items: (r.items || []).map(normalizeTrend) };
  },
  /** Top TikTok videos using a song — the trending list doesn't carry them, so the drawer loads them on open. */
  trendVideos: async (trendId: string, region = DEFAULT_TREND_REGION) => {
    const r = await request<{ trend_id: string; top_videos: TrendTopVideo[] }>('GET', studio(`/trends/audio/${encodeURIComponent(trendId)}/videos`), { query: { region } });
    return r.top_videos ?? [];
  },
  /** `region` / `period` tell the service which cached trends list holds `trendId` (defaults US / 7). */
  trendLookalike: async (trendId: string, body: LookalikeBody = {}, where: { region?: string; period?: number } = {}) =>
    normalizeJob(await request<StudioJob>('POST', studio(`/trends/audio/${encodeURIComponent(trendId)}/lookalike`), { body, query: where })),
  // ── Collections (team-scoped named groups of studio sounds) ──
  /** Most recently updated first. */
  collections: async (params: { brand_id?: string } = {}) => {
    const r = await request<{ collections: CollectionSummary[] }>('POST', studio('/collections/search'), { query: params });
    return (r.collections || []).map(normalizeCollection);
  },
  /** 409 when a collection with that name (case-insensitive) already exists. */
  createCollection: async (body: { name: string; track_ids?: string[]; brand_id?: string }) =>
    normalizeCollection(await request<CollectionSummary>('POST', studio('/collection/create'), { body })),
  collection: async (id: string) => normalizeCollectionDetail(await request<CollectionDetail>('GET', studio(`/collection/${encodeURIComponent(id)}`))),
  /** `brand_id: ""` clears the brand. */
  updateCollection: async (id: string, patch: { name?: string; brand_id?: string }) =>
    normalizeCollection(await request<CollectionSummary>('POST', studio(`/collection/${encodeURIComponent(id)}/update`), { body: patch })),
  /** The sounds themselves are kept. */
  deleteCollection: (id: string) => request<void>('POST', studio(`/collection/${encodeURIComponent(id)}/archive`)),
  /** Duplicates are ignored by the service. */
  addToCollection: async (id: string, trackIds: string[]) =>
    normalizeCollection(await request<CollectionSummary>('POST', studio(`/collections/${encodeURIComponent(id)}/tracks`), { body: { track_ids: trackIds } })),
  removeFromCollection: async (id: string, trackId: string) =>
    normalizeCollection(await request<CollectionSummary>('DELETE', studio(`/collections/${encodeURIComponent(id)}/tracks/${encodeURIComponent(trackId)}`))),
  /** A library track's public page (/sound/:id): the curated catalogue is public by track id. */
  publicLibraryTrack: async (id: string): Promise<PublicStudioTrack> =>
    libraryToPublicTrack(await request<LibraryTrack>('GET', studio(`/library/track-by-id/${encodeURIComponent(id)}`), { auth: false })),
  publicTrack: async (id: string, token: string): Promise<PublicStudioTrack> => {
    const t = await request<PublicStudioTrack>('GET', studio(`/public/track/${encodeURIComponent(id)}`), { query: { token }, auth: false });
    return { ...t, audio_url: resolveMediaUrl(t.audio_url) || '', cover_url: resolveMediaUrl(t.cover_url) };
  },
  library: {
    tracks: async (params: LibraryTracksParams = {}) => {
      const r = await request<LibraryTracksResponse>('GET', studio('/library/tracks'), { query: { limit: 20, offset: 0, ...params }, auth: false });
      return { tracks: r.tracks || [], total: r.total ?? 0 };
    },
    filterOptions: async () => {
      const r = await request<Partial<LibraryFilterOptions>>('GET', studio('/library/filter-options'), { auth: false });
      return { genres: (r.genres || []).filter(Boolean), moods: (r.moods || []).filter(Boolean), tempos: (r.tempos || []).filter(Boolean) };
    },
    playlists: async () => {
      const r = await request<{ playlists?: LibraryPlaylist[] }>('GET', studio('/playlists'), { auth: false });
      return r.playlists || [];
    },
    playlist: (id: string) => request<LibraryPlaylist>('GET', studio(`/playlists/${encodeURIComponent(id)}`), { auth: false }),
  },
};

export type AudioStudioApi = typeof audioStudioApi;
